import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/LanguageContext';
import { PublicApi } from '../../utils/apiClient';
import { Lang, PublicProduct } from '../../types';
import { fallbackProducts } from '../../data/fallback';
import { Cylinder } from 'lucide-react';
import FadeIn from './FadeIn';

/** How many products the home page shows when items are flagged featured. */
export const HOME_PRODUCTS = 6;

const HINT: Record<Lang, string> = {
  de: 'Fotos ansehen',
  en: 'View photos',
  it: 'Guarda le foto',
};

const PG = ['pg-1', 'pg-2', 'pg-6', 'pg-3', 'pg-4', 'pg-5', 'pg-7'];

export const ProductCard: React.FC<{ p: PublicProduct; i: number; onOpen: (imgs: string[]) => void }> = ({ p, i, onOpen }) => {
  const { lang } = useI18n();
  const [imgFailed, setImgFailed] = useState(false);
  const clickable = (p.gallery?.length || 0) > 0;
  const delay = `${(i % 6) * 0.05 + (i >= 6 ? 0.3 : 0)}s`;

  return (
    <FadeIn
      className={`product-card${clickable ? ' clickable' : ''}`}
      style={{ transitionDelay: delay, cursor: clickable ? 'pointer' : 'default' }}
      onClick={clickable ? () => onOpen(p.gallery) : undefined}
      data-hint={clickable ? HINT[lang] : undefined}
      title={clickable ? HINT[lang] : undefined}
    >
      <div className="pimg-wrap">
        <div className={`pimg-fallback ${PG[i % PG.length]}`}>
          <Cylinder className="pimg-icon" size={44} aria-hidden />
          <span>{p.title}</span>
        </div>
        {p.previewUrl && !imgFailed && (
          <img src={p.previewUrl} alt={p.title} width={400} height={215} loading="lazy" decoding="async" onError={() => setImgFailed(true)} />
        )}
      </div>
      <div className="product-body">
        {p.tag && <span className="ptag">{p.tag}</span>}
        <h3 className="ptitle">{p.title}</h3>
        <p className="pdesc">{p.desc}</p>
      </div>
    </FadeIn>
  );
};

const Products: React.FC<{ onOpenGallery: (images: string[]) => void }> = ({ onOpenGallery }) => {
  const { t, lang } = useI18n();
  const [items, setItems] = useState<PublicProduct[]>(() => fallbackProducts(lang));

  useEffect(() => {
    let cancelled = false;
    setItems(fallbackProducts(lang));
    PublicApi.products(lang)
      .then((d) => { if (!cancelled && d && d.length) setItems(d); })
      .catch(() => { if (!cancelled) setItems(fallbackProducts(lang)); });
    return () => { cancelled = true; };
  }, [lang]);

  // Home shows featured products; if none are flagged (e.g. bundled/prerender data), show the first N.
  const featured = items.filter((p) => p.featured);
  const home = featured.length ? featured.slice(0, HOME_PRODUCTS) : items.slice(0, HOME_PRODUCTS);

  return (
    <section id="products">
      <div className="container">
        <FadeIn>
          <span className="slabel sl-b">{t('prod.badge')}</span>
          <h2 className="stitle">{t('prod.title')}</h2>
          <p className="ssub">{t('prod.sub')}</p>
        </FadeIn>
        <div className="products-grid">
          {home.map((p, i) => <ProductCard key={p.id} p={p} i={i} onOpen={onOpenGallery} />)}
        </div>
        {items.length > 0 && (
          <div className="catalog-cta">
            <Link to="/prodotti" className="btn btn-p">{t('prod.viewAll')}</Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Products;
